import { useEffect, useRef, useState, type RefObject } from 'react';
import type L from 'leaflet';
import { useUIStore } from '../../store/uiStore';
import { useMapOverlaysStore } from '../../store/mapOverlaysStore';
import { usePlanMapPlacementStore } from '../../store/planMapPlacementStore';
import { getLeaflet } from './leafletHelpers';
import {
  ensurePlanMapsPane,
  ensurePlanMapHandlesPane,
  PLAN_MAPS_PANE,
  PLAN_MAP_HANDLES_PANE,
} from './useMapOverlaysLayer.web';
import { PLAN_PANEL_WIDTH } from '../../theme/layout';
import {
  fitCornersToBounds,
  bottomRightLatLng,
  moveCorners,
  setCorner,
  type CornerKey,
  type OverlayCorners,
} from '../../utils/overlayCorners';

// Extra breathing room (px) beyond the dock width itself, so a handle never
// sits flush against the panel's edge or the window edge.
const FIT_PADDING_PX = 32;

// How much of the visible map a freshly seeded import covers, and how much
// "Fit to view" gives it. Fit is the deliberate gesture, so it fills more.
const SEED_FRACTION = 0.7;
const FIT_TO_VIEW_FRACTION = 0.9;

const HANDLE_SIZE_PX = 16;
const DOT_SIZE_PX = 10;
// Not themed — this is moderator-only map chrome built outside React (same
// as PLAN_CASING/FHSZ_TIER_COLORS elsewhere in this directory), so there's
// no useTheme() to reach for. Ink fill, white ring: reads against both the
// standard and satellite basemaps in either app theme.
// eslint-disable-next-line no-restricted-syntax -- feeds a Leaflet divIcon HTML string outside the themed React tree
const HANDLE_COLOR = '#161615';

function handleIcon(Leaflet: typeof L): L.DivIcon {
  return Leaflet.divIcon({
    className: 'plan-map-corner-handle',
    html:
      `<div style="width:${HANDLE_SIZE_PX}px;height:${HANDLE_SIZE_PX}px;border-radius:50%;` +
      `background:${HANDLE_COLOR};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.4);cursor:grab;"></div>`,
    iconSize: [HANDLE_SIZE_PX, HANDLE_SIZE_PX],
    iconAnchor: [HANDLE_SIZE_PX / 2, HANDLE_SIZE_PX / 2],
  });
}

function dotIcon(Leaflet: typeof L): L.DivIcon {
  return Leaflet.divIcon({
    className: 'plan-map-corner-dot',
    html:
      `<div style="width:${DOT_SIZE_PX}px;height:${DOT_SIZE_PX}px;border-radius:50%;` +
      `background:${HANDLE_COLOR};opacity:0.55;border:1.5px solid #fff;"></div>`,
    iconSize: [DOT_SIZE_PX, DOT_SIZE_PX],
    iconAnchor: [DOT_SIZE_PX / 2, DOT_SIZE_PX / 2],
  });
}

/**
 * Upright corners that fit the image into the part of the map the moderator
 * can actually see — the strip under the left dock (the placement panel
 * itself) is excluded, so a rectangle centered on the full container can't
 * seed half-hidden. Used both to seed a fresh import and by the panel's
 * "Fit to view", which is the only way to re-place a map without dragging a
 * corner the whole width of the screen.
 */
function cornersFromVisibleArea(
  Leaflet: typeof L,
  map: L.Map,
  imageWidth: number,
  imageHeight: number,
  fraction: number
): OverlayCorners {
  const size = map.getSize();
  const nw = map.containerPointToLatLng(Leaflet.point(PLAN_PANEL_WIDTH, 0));
  const se = map.containerPointToLatLng(Leaflet.point(size.x, size.y));
  return fitCornersToBounds(
    { north: nw.lat, south: se.lat, east: se.lng, west: nw.lng },
    imageWidth,
    imageHeight,
    fraction
  );
}

interface Handles {
  topLeft: L.Marker;
  topRight: L.Marker;
  bottomLeft: L.Marker;
  bottomRightDot: L.Marker;
}

/**
 * The Leaflet side of the plan-map placement editor (PlanMapPlacementPanel
 * .web.tsx is the form side): while `uiStore.placingOverlayId` is set, draws
 * three draggable corner handles plus a read-only dot at the fourth corner,
 * and lets the moderator drag the image body itself to move every corner
 * together. Both gestures write to `planMapPlacementStore`, never straight
 * to Firestore — the panel's Save commits it.
 *
 * Renders no image of its own: useMapOverlaysLayer.web.ts already draws the
 * overlay being placed (reading this same store for its live corners/
 * opacity once placement starts), so this hook only adds the handles and
 * the body-drag gesture on top of that shared layer, found by the
 * `data-overlay-id` attribute the other hook sets on its <img> element.
 */
export function usePlanMapPlacementLayer({
  mapRef,
  isLoaded,
}: {
  mapRef: RefObject<L.Map | null>;
  isLoaded: boolean;
}): void {
  const placingOverlayId = useUIStore((s) => s.placingOverlayId);
  const placingFitToView = useUIStore((s) => s.placingOverlayFitToView);
  const overlay = useMapOverlaysStore((s) =>
    placingOverlayId ? (s.overlaysById[placingOverlayId] ?? null) : null
  );

  const handlesRef = useRef<Handles | null>(null);
  // Guards the corners seed (fit-to-view vs. saved) to run once per
  // placement session, not on every re-render this effect happens to see
  // (e.g. an unrelated overlaysById update while the session is open).
  const initializedIdRef = useRef<string | null>(null);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded || !placingOverlayId || !overlay) return;
    ensurePlanMapsPane(map);
    ensurePlanMapHandlesPane(map);
    const Leaflet = getLeaflet();
    const store = usePlanMapPlacementStore;

    if (initializedIdRef.current !== placingOverlayId) {
      initializedIdRef.current = placingOverlayId;

      if (placingFitToView) {
        // Seed a NEW import from the visible map area, at 0.7 so there's
        // room to drag a corner outward without leaving the screen.
        const corners = cornersFromVisibleArea(
          Leaflet,
          map,
          overlay.imageWidth,
          overlay.imageHeight,
          SEED_FRACTION
        );
        store.getState().initialize(corners, overlay.opacity);
      } else {
        // Placing an EXISTING map: fit the view to its four corners so all
        // three handles land clear of the dock, instead of trusting
        // whatever the map happened to be centered on when the moderator
        // opened the editor.
        const corners = overlay.corners;
        const cornerLatLngs: L.LatLngExpression[] = [
          corners.topLeft,
          corners.topRight,
          corners.bottomLeft,
          bottomRightLatLng(corners),
        ];
        map.fitBounds(Leaflet.latLngBounds(cornerLatLngs), {
          paddingTopLeft: [PLAN_PANEL_WIDTH + FIT_PADDING_PX, FIT_PADDING_PX],
          paddingBottomRight: [FIT_PADDING_PX, FIT_PADDING_PX],
        });
        store.getState().initialize(corners, overlay.opacity);
      }
    }

    const currentCorners = (): OverlayCorners => store.getState().corners ?? overlay.corners;

    const onHandleDrag = (key: CornerKey) => (e: L.LeafletEvent) => {
      const marker = e.target as L.Marker;
      store.getState().setCorners(setCorner(currentCorners(), key, marker.getLatLng()));
    };

    const makeHandle = (
      latlng: L.LatLngExpression,
      draggable: boolean,
      icon: L.DivIcon
    ): L.Marker =>
      Leaflet.marker(latlng, {
        icon,
        draggable,
        pane: PLAN_MAP_HANDLES_PANE,
        keyboard: false,
        interactive: draggable,
      }).addTo(map);

    const seed = currentCorners();
    const topLeft = makeHandle(seed.topLeft, true, handleIcon(Leaflet));
    const topRight = makeHandle(seed.topRight, true, handleIcon(Leaflet));
    const bottomLeft = makeHandle(seed.bottomLeft, true, handleIcon(Leaflet));
    const bottomRightDot = makeHandle(bottomRightLatLng(seed), false, dotIcon(Leaflet));

    topLeft.on('drag', onHandleDrag('topLeft'));
    topRight.on('drag', onHandleDrag('topRight'));
    bottomLeft.on('drag', onHandleDrag('bottomLeft'));

    handlesRef.current = { topLeft, topRight, bottomLeft, bottomRightDot };

    // Body drag: mousedown on the shared <img> (owned by
    // useMapOverlaysLayer.web.ts) moves every corner by the same lat/lng
    // delta. Hand-rolled the same way useDefensePlanDraw's vertex gesture
    // is — the image is a plain element, not an L.Marker, so there's no
    // Leaflet draggable to lean on. The element may not exist on this
    // effect's very first tick (the rendering hook's own effect runs
    // after this one on first mount) — a rAF retry avoids depending on it
    // and re-running this whole setup once it appears.
    let detachBodyDrag: (() => void) | null = null;
    const attachBodyDrag = () => {
      const img = map
        .getPane(PLAN_MAPS_PANE)
        ?.querySelector<HTMLImageElement>(`img[data-overlay-id="${placingOverlayId}"]`);
      if (!img) return;
      // Move image / Move map starts in 'image' mode (initialize() always
      // resets it), matching the default pointer-events the element already
      // has — this just keeps it explicit for the mode-sync effect below.
      img.style.pointerEvents = store.getState().mode === 'map' ? 'none' : 'auto';
      const onMouseDown = (downEvent: MouseEvent) => {
        downEvent.preventDefault();
        map.dragging.disable();
        const startPoint = map.mouseEventToLatLng(downEvent);
        const startCorners = currentCorners();
        const onMouseMove = (moveEvent: MouseEvent) => {
          const nowPoint = map.mouseEventToLatLng(moveEvent);
          store
            .getState()
            .setCorners(
              moveCorners(
                startCorners,
                nowPoint.lat - startPoint.lat,
                nowPoint.lng - startPoint.lng
              )
            );
        };
        const onMouseUp = () => {
          map.dragging.enable();
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      };
      img.addEventListener('mousedown', onMouseDown);
      detachBodyDrag = () => img.removeEventListener('mousedown', onMouseDown);
    };
    const raf = requestAnimationFrame(attachBodyDrag);

    return () => {
      cancelAnimationFrame(raf);
      detachBodyDrag?.();
      topLeft.remove();
      topRight.remove();
      bottomLeft.remove();
      bottomRightDot.remove();
      handlesRef.current = null;
    };
  }, [placingOverlayId, overlay, placingFitToView, isLoaded, mapRef]);

  // Move image / Move map (panel's segmented control) toggles whether the
  // shared <img> reacts to pointer events at all: 'map' mode (or holding
  // Space) makes it transparent to the mouse so the same drag pans the
  // Leaflet map underneath instead of moving the overlay. The corner
  // handles are separate Leaflet markers, added with their own
  // `draggable`/`interactive` flags above, so they stay draggable in both
  // modes regardless of this.
  const mode = usePlanMapPlacementStore((s) => s.mode);
  const [spaceHeld, setSpaceHeld] = useState(false);

  useEffect(() => {
    if (!placingOverlayId) return;
    // Only when focus is on the map or on nothing: Space on a focused button,
    // switch or field must keep doing what it does there (press, toggle, type).
    const isMapOrBody = (target: EventTarget | null): boolean =>
      target === document.body ||
      (target instanceof HTMLElement && !!target.closest('.leaflet-container'));
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat || !isMapOrBody(e.target)) return;
      e.preventDefault();
      setSpaceHeld(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      setSpaceHeld(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      setSpaceHeld(false);
    };
  }, [placingOverlayId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !placingOverlayId) return;
    const img = map
      .getPane(PLAN_MAPS_PANE)
      ?.querySelector<HTMLImageElement>(`img[data-overlay-id="${placingOverlayId}"]`);
    if (img) img.style.pointerEvents = mode === 'map' || spaceHeld ? 'none' : 'auto';
  }, [mode, spaceHeld, placingOverlayId, mapRef]);

  // Keep the handles positioned on whatever `corners` last became —
  // whether from a handle's own drag (a harmless no-op setLatLng back onto
  // itself), the body drag above, or the panel's Reset button.
  const corners = usePlanMapPlacementStore((s) => s.corners);
  useEffect(() => {
    const handles = handlesRef.current;
    if (!handles || !corners) return;
    handles.topLeft.setLatLng(corners.topLeft);
    handles.topRight.setLatLng(corners.topRight);
    handles.bottomLeft.setLatLng(corners.bottomLeft);
    handles.bottomRightDot.setLatLng(bottomRightLatLng(corners));
  }, [corners]);

  // "Fit to view": drop the image onto whatever the map is showing, upright
  // and unrotated, keeping its aspect. The only way to re-place a map at a
  // zoom where its corners are off-screen or on top of each other — dragging
  // a handle can't cross a distance the screen doesn't hold. `initialize`
  // and `clear` both reset the counter to 0, so 0 always means "not asked".
  const fitRequested = usePlanMapPlacementStore((s) => s.fitRequested);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !fitRequested || !overlay) return;
    const Leaflet = getLeaflet();
    usePlanMapPlacementStore
      .getState()
      .setCorners(
        cornersFromVisibleArea(
          Leaflet,
          map,
          overlay.imageWidth,
          overlay.imageHeight,
          FIT_TO_VIEW_FRACTION
        )
      );
  }, [fitRequested, overlay, mapRef]);

  // Placement ended (Save/Publish/Cancel) — drop the working state so a
  // later session never opens onto a previous overlay's corners. The
  // handles themselves are torn down by the build effect's own cleanup,
  // which also runs on this same `placingOverlayId` transition.
  useEffect(() => {
    if (placingOverlayId) return;
    initializedIdRef.current = null;
    usePlanMapPlacementStore.getState().clear();
  }, [placingOverlayId]);
}
